/**
 * AIAutomation — Phase 10 dashboard.
 *
 * Two panels:
 *   1) Document extraction — drag/click a PDF or image, POST as multipart to
 *      `/api/ai-automation/analyze-document`, render the structured JSON
 *      Gemini pulled out (supplier / product / invoice_number / carbon_value
 *      / unit / date / category / confidence).
 *   2) Anomaly feed — `/api/ai-automation/anomalies` returns real-data flags
 *      (CBAM spikes, inactive suppliers, evidence backlog, quality issues).
 *
 * RBAC: /ai-automation is gated to admin/editor by RoleRoute upstream.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Upload, Loader2, Bot, ScanLine, AlertTriangle, RefreshCw, FileText,
  CheckCircle2, XCircle, Sparkles, ClipboardCopy,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const SEVERITY = {
  high:   'bg-red-500/10 text-red-700 border-red-500/30',
  medium: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  low:    'bg-slate-500/10 text-slate-700 border-slate-500/30',
};

const CATEGORY = {
  invoice:               'bg-blue-500/10 text-blue-700 border-blue-500/30',
  energy_data:           'bg-amber-500/10 text-amber-700 border-amber-500/30',
  supplier_declaration:  'bg-emerald-500/10 text-emerald-700 border-emerald-500/30',
  transport:             'bg-purple-500/10 text-purple-700 border-purple-500/30',
  certificate:           'bg-indigo-500/10 text-indigo-700 border-indigo-500/30',
  other:                 'bg-slate-500/10 text-slate-700 border-slate-500/30',
};

const ALLOWED = 'application/pdf,image/png,image/jpeg,image/webp,image/heic,image/heif';

function fmtBytes(n) {
  const v = Number(n || 0);
  if (v >= 1024 * 1024) return `${(v / (1024*1024)).toFixed(1)} MB`;
  if (v >= 1024)        return `${(v / 1024).toFixed(1)} KB`;
  return `${v} B`;
}

export default function AIAutomation() {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [loadingFeed, setLoadingFeed] = useState(true);
  const inputRef = useRef(null);

  const loadFeed = useCallback(async () => {
    setLoadingFeed(true);
    try {
      const [a, e] = await Promise.all([
        api.get('/ai-automation/anomalies'),
        api.get('/ai-automation/extractions', { params: { limit: 10 } }),
      ]);
      setAnomalies(a.data?.anomalies || []);
      setHistory(e.data?.extractions || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load AI feed');
    } finally { setLoadingFeed(false); }
  }, []);
  useEffect(() => { loadFeed(); }, [loadFeed]);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error('File too large (max 12 MB)');
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/ai-automation/analyze-document', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(r.data);
      toast.success(`Extracted with ${r.data.engine} · ${r.data.extraction.confidence}% confidence`);
      loadFeed();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Extraction failed');
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (uploading) return;
    const f = e.dataTransfer?.files?.[0];
    if (f) handleFile(f);
  };

  const copyJSON = () => {
    if (!result?.extraction) return;
    navigator.clipboard.writeText(JSON.stringify(result.extraction, null, 2));
    toast.success('JSON copied to clipboard');
  };

  const engineBadge = useMemo(() => {
    if (!result) return null;
    const isGemini = String(result.engine || '').startsWith('gemini');
    return { text: result.engine, ok: isGemini };
  }, [result]);

  return (
    <div className="p-6 pb-32 max-w-7xl mx-auto space-y-6" data-testid="ai-automation-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h1 className="text-2xl font-heading font-bold" data-testid="ai-automation-title">
              AI Automation Layer
            </h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Gemini 2.5 Flash Vision · document extraction · real-data anomaly detection
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadFeed} data-testid="ai-automation-refresh-btn">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Upload panel — spans 2 cols */}
        <Card className="lg:col-span-2 p-5" data-testid="ai-automation-upload-card">
          <div className="flex items-center gap-2 mb-3">
            <ScanLine className="w-4 h-4 text-purple-600" />
            <div className="font-heading font-semibold text-sm">Document extraction</div>
          </div>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => !uploading && inputRef.current?.click()}
            className={`rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition
              ${dragOver ? 'border-purple-500 bg-purple-500/5' : 'border-muted-foreground/30 hover:border-purple-500/60'}
              ${uploading ? 'opacity-50 cursor-wait' : ''}`}
            data-testid="ai-automation-dropzone"
          >
            <input
              ref={inputRef}
              type="file"
              accept={ALLOWED}
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="ai-automation-file-input"
            />
            {uploading ? (
              <div className="space-y-2">
                <Loader2 className="w-8 h-8 mx-auto text-purple-600 animate-spin" />
                <div className="text-sm text-muted-foreground">Analysing with Gemini Vision…</div>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="w-8 h-8 mx-auto text-muted-foreground" />
                <div className="text-sm font-medium">Drop a PDF or image, or click to browse</div>
                <div className="text-[11px] text-muted-foreground">
                  Supported: PDF, PNG, JPEG, WEBP, HEIC · Max 12 MB
                </div>
              </div>
            )}
          </div>

          {result && (
            <div className="mt-4 border rounded-lg p-4 space-y-3" data-testid="ai-automation-result">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText className="w-4 h-4 shrink-0 text-muted-foreground" />
                  <span className="font-medium text-sm truncate" data-testid="ai-automation-result-filename">
                    {result.file_name}
                  </span>
                  <span className="text-[10px] text-muted-foreground shrink-0">
                    {fmtBytes(result.file_bytes_size)}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {engineBadge && (
                    <Badge variant="outline" className={`text-[10px] ${engineBadge.ok ? 'border-purple-500/40 text-purple-700' : 'border-amber-500/40 text-amber-700'}`}
                           data-testid="ai-automation-result-engine">
                      {engineBadge.ok ? <Bot className="w-3 h-3 mr-1 inline"/> : null}
                      {engineBadge.text}
                    </Badge>
                  )}
                  <Badge variant="outline" className="text-[10px]" data-testid="ai-automation-result-confidence">
                    {result.extraction.confidence}% conf
                  </Badge>
                  <Badge className={`text-[10px] ${CATEGORY[result.extraction.category] || CATEGORY.other}`}>
                    {result.extraction.category}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                {[
                  ['Supplier',        result.extraction.supplier],
                  ['Product',         result.extraction.product],
                  ['Invoice #',       result.extraction.invoice_number],
                  ['Carbon value',    result.extraction.carbon_value != null
                                        ? `${result.extraction.carbon_value} ${result.extraction.unit || ''}`
                                        : null],
                  ['Document date',   result.extraction.date],
                  ['Notes',           result.extraction.notes],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between gap-3 border-b border-dashed pb-1"
                       data-testid={`ai-automation-field-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    <span className="text-muted-foreground text-xs">{label}</span>
                    <span className="text-right font-medium">
                      {value ? (
                        <>{value}<CheckCircle2 className="w-3 h-3 inline ml-1 text-emerald-600"/></>
                      ) : (
                        <span className="text-muted-foreground italic">not detected<XCircle className="w-3 h-3 inline ml-1"/></span>
                      )}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex justify-end">
                <Button size="sm" variant="outline" onClick={copyJSON} data-testid="ai-automation-copy-json-btn">
                  <ClipboardCopy className="w-3.5 h-3.5 mr-1" /> Copy JSON
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Anomalies panel */}
        <Card className="p-5" data-testid="ai-automation-anomalies-card">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <div className="font-heading font-semibold text-sm">Anomaly feed</div>
          </div>
          {loadingFeed ? (
            <div className="text-center py-6 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Scanning…
            </div>
          ) : anomalies.length === 0 ? (
            <div className="text-center py-6 text-sm text-emerald-700 dark:text-emerald-400"
                 data-testid="ai-automation-anomalies-empty">
              <CheckCircle2 className="w-5 h-5 inline mr-1" />
              No anomalies detected. Nice work.
            </div>
          ) : (
            <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1"
                 data-testid="ai-automation-anomalies-list">
              {anomalies.map((a, i) => (
                <div key={`${a.type}-${i}`}
                     className={`rounded-md border px-3 py-2 text-xs ${SEVERITY[a.severity] || SEVERITY.low}`}
                     data-testid={`ai-automation-anomaly-${a.type}-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold uppercase tracking-wide text-[10px]">{a.type}</span>
                    <span className="text-[10px] opacity-80">{a.severity}</span>
                  </div>
                  <div className="mt-1 text-[12px] opacity-95">{a.description}</div>
                  <div className="text-[11px] mt-1 opacity-75">→ {a.recommendation}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Recent extractions */}
      <Card className="p-0 overflow-hidden">
        <div className="px-5 py-3 border-b">
          <div className="font-heading font-semibold text-sm">Recent extractions</div>
          <div className="text-[11px] text-muted-foreground">
            {loadingFeed ? 'Loading…' : `${history.length} shown · org-scoped, newest first`}
          </div>
        </div>
        {loadingFeed ? (
          <div className="py-6 text-center text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…
          </div>
        ) : history.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground" data-testid="ai-automation-history-empty">
            No extractions yet. Upload a document above to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="ai-automation-history-table">
              <thead className="bg-muted/40 text-left">
                <tr>
                  <th className="px-4 py-2 text-xs uppercase tracking-wide text-muted-foreground">File</th>
                  <th className="px-4 py-2 text-xs uppercase tracking-wide text-muted-foreground">Category</th>
                  <th className="px-4 py-2 text-xs uppercase tracking-wide text-muted-foreground">Supplier</th>
                  <th className="px-4 py-2 text-xs uppercase tracking-wide text-muted-foreground">Carbon</th>
                  <th className="px-4 py-2 text-xs uppercase tracking-wide text-muted-foreground">Engine</th>
                  <th className="px-4 py-2 text-xs uppercase tracking-wide text-muted-foreground">Conf.</th>
                  <th className="px-4 py-2 text-xs uppercase tracking-wide text-muted-foreground">When</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {history.map((h) => (
                  <tr key={h.id} className="hover:bg-muted/30" data-testid={`ai-automation-history-row-${h.id}`}>
                    <td className="px-4 py-2 truncate max-w-[240px]">{h.file_name}</td>
                    <td className="px-4 py-2">
                      <Badge className={`text-[10px] ${CATEGORY[h.extraction?.category] || CATEGORY.other}`}>
                        {h.extraction?.category || 'other'}
                      </Badge>
                    </td>
                    <td className="px-4 py-2 text-xs">{h.extraction?.supplier || '—'}</td>
                    <td className="px-4 py-2 text-xs">
                      {h.extraction?.carbon_value != null
                        ? `${h.extraction.carbon_value} ${h.extraction.unit || ''}`
                        : '—'}
                    </td>
                    <td className="px-4 py-2 text-[11px] font-mono text-muted-foreground">{h.engine}</td>
                    <td className="px-4 py-2 text-xs">{h.extraction?.confidence ?? 0}%</td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {new Date(h.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
